import { TFunction } from "i18next";
import { getModule } from "@companion-core/shared/app/Utils/config";
import getValidation from "@companion-core/shared/app/Utils/validators";
import { trackClickLogin } from "@companion-core/shared/app/tracking/login";
import { useAuthStore } from "@companion-core/shared/app/Store/authStore";

interface ProcessLoginProps {
  username: string;
  password: string;
  alreadyHashed?: boolean;
  t: TFunction<"translation", undefined>;
}

/**
 * Processes a login request.
 *
 * @async
 * @function processLogin
 * @param {ProcessLoginProps} props - The login process properties.
 * @param {string} props.username - The username for the login.
 * @param {string} props.password - The password for the login.
 * @param {boolean} [props.alreadyHashed=false] - Indicates if the password is already hashed.
 * @param {Function} props.t - The translation function.
 * @returns {Promise<Object>} A Promise that resolves to an object with the login result.
 * @returns {boolean} .success - Indicates if the login was successful.
 * @returns {string} .hash - The hash value associated with the login.
 */
export const processLogin = async ({
  username,
  password,
  alreadyHashed = false,
  t,
}: ProcessLoginProps): Promise<{ success: boolean; hash: string }> => {
  let result = false;
  let hashResult = "";
  let trackingError: string | null = null;
  const trimmedUsername: string = username?.trim();

  if (username === "" || password === "") {
    trackingError = "empty_form";
    useAuthStore.getState().setError(t("login.error.form.empty.error"));
  } else {
    const { isValid, errorMessages } = getValidation(
      getModule("login").options?.username?.validators,
      trimmedUsername
    );

    if (!isValid) {
      useAuthStore.getState().setError(t(`${errorMessages[0]}.text`));
      trackingError = "bad_username_format";
    } else {
      useAuthStore.getState().setError(null);
      const { success, hash, message } = await useAuthStore
        .getState()
        .authAction(trimmedUsername, password, alreadyHashed);
      result = success;
      hashResult = hash ?? "";
      if (message) trackingError = message;
    }
  }

  trackClickLogin(result, trackingError);
  return { success: result, hash: hashResult };
};
